import {
  ROTULO_DA_VISTA,
  VISTAS_DA_FACE,
  formatarQuantidade,
  regiaoDaFace,
  resumoDaFace,
  valorPorExtenso,
  type MarcacaoFacial,
  type ModeloDaFace,
} from "@bookdental/schemas";
import { esc } from "@/components/production/impressao";
import { modeloDoPlano } from "@/components/face/desenhos";
import { svgDaFace, type MarcaImpressa } from "@/components/face/FaceEstatica";
import { COR_NEUTRA } from "./cores-do-plano";

export type DadosDoOrcamento = {
  numero: number;
  titulo: string | null;
  criadoEm: Date | string;
  validadeAte: Date | string | null;
  observacoes: string | null;
  dentista: string | null;
  paciente: { name: string; document: string | null; sex: string | null };
  modeloDaFace: ModeloDaFace | null;
  clinica: { nome: string; documento: string | null; telefone: string | null; endereco: string };
  itens: {
    nome: string;
    dentes: string | null;
    precoCents: number | null;
    porDente: boolean;
    quantidade: number;
    unidade: string | null;
    offeredService: { color: string | null } | null;
    marcacoes: MarcacaoFacial[];
  }[];
  subtotal: number;
  desconto: number;
  total: number;
};

const r = (c: number | null) => (c == null ? "—" : (c / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }));
const data = (d: Date | string) => new Date(d).toLocaleDateString("pt-BR");

/** Orçamento A4 para o paciente: clínica, itens, total por extenso, validade e assinaturas. */
export function htmlDoOrcamento(d: DadosDoOrcamento) {
  const linhas = d.itens
    .map((i) => {
      const valor = i.porDente && i.quantidade > 1 && i.precoCents != null
        ? `${i.quantidade} × ${r(i.precoCents)}<br><b>${r(i.precoCents * i.quantidade)}</b>`
        : r(i.precoCents);
      const regiao = [i.dentes, resumoDaFace(i.marcacoes, i.unidade)].filter(Boolean).join(" · ");
      return `<tr><td>${esc(i.nome)}</td><td>${esc(regiao)}</td><td class="v">${valor}</td></tr>`;
    })
    .join("");
  return `<div class="pg">
<header><div><h1>${esc(d.clinica.nome)}</h1>
<p>${[d.clinica.documento, d.clinica.telefone].filter(Boolean).map((x) => esc(x!)).join(" · ")}</p>
<p>${esc(d.clinica.endereco)}</p></div>
<div class="num"><b>Orçamento nº ${d.numero}</b><span>${data(d.criadoEm)}</span></div></header>
<section class="pac"><p><b>Paciente:</b> ${esc(d.paciente.name)}</p>
${d.dentista ? `<p><b>Cirurgião-dentista:</b> ${esc(d.dentista)}</p>` : ""}
${d.titulo ? `<p><b>Plano:</b> ${esc(d.titulo)}</p>` : ""}</section>
<table><thead><tr><th>Procedimento</th><th>Dentes / região</th><th class="v">Valor</th></tr></thead><tbody>${linhas}</tbody></table>
<div class="tot">
${d.desconto ? `<p>Subtotal <span>${r(d.subtotal)}</span></p><p>Desconto <span>− ${r(d.desconto)}</span></p>` : ""}
<p class="t">Total <span>${r(d.total)}</span></p>
<p class="ext">${esc(valorPorExtenso(d.total))}</p></div>
${planejamentoFacial(d)}
${d.observacoes ? `<p class="obs"><b>Observações:</b> ${esc(d.observacoes)}</p>` : ""}
${d.validadeAte ? `<p class="obs">Orçamento válido até ${data(d.validadeAte)}.</p>` : ""}
<div class="ass"><div>Cirurgião-dentista</div><div>Paciente ou responsável</div></div>
</div>`;
}

/** As vistas com marcação, numeradas na ordem dos itens, e a legenda. Vazio quando não há face. */
function planejamentoFacial(d: DadosDoOrcamento) {
  const marcas: (MarcaImpressa & { item: DadosDoOrcamento["itens"][number] })[] = [];
  for (const item of d.itens) {
    for (const m of item.marcacoes) marcas.push({ m, item, cor: item.offeredService?.color || COR_NEUTRA, numero: marcas.length + 1 });
  }
  if (!marcas.length) return "";
  const modelo = modeloDoPlano(d.modeloDaFace, d.paciente.sex);
  const vistas = VISTAS_DA_FACE.filter((v) => marcas.some((k) => k.m.vista === v))
    .map((v) => `<figure>${svgDaFace(modelo, v, marcas.filter((k) => k.m.vista === v))}<figcaption>${ROTULO_DA_VISTA[v]}</figcaption></figure>`)
    .join("");
  const legenda = marcas
    .map(
      (k) =>
        `<tr><td>${k.numero}</td><td><i style="background:${k.cor}"></i>${esc(k.item.nome)}</td><td>${esc(regiaoDaFace(k.m.regiao)?.rotulo ?? "—")}</td><td>${k.m.quantidade != null ? esc(formatarQuantidade(k.m.quantidade, k.item.unidade)) : "—"}</td><td>${esc(k.m.observacao ?? "")}</td></tr>`,
    )
    .join("");
  return `<section class="face"><h2>Planejamento facial</h2><div class="vistas">${vistas}</div>
<table class="leg"><thead><tr><th>Nº</th><th>Procedimento</th><th>Região</th><th>Quantidade</th><th>Observação</th></tr></thead><tbody>${legenda}</tbody></table></section>`;
}

export const CSS_DO_ORCAMENTO = `
@page { size: A4; margin: 16mm; }
.pg { font-size: 12px; }
header { display: flex; justify-content: space-between; border-bottom: 2px solid #000; padding-bottom: 8px; }
h1 { font-size: 18px; margin: 0 0 4px; }
header p { margin: 1px 0; color: #333; }
.num { text-align: right; } .num b { display: block; font-size: 14px; } .num span { color: #333; }
.pac { margin: 14px 0; } .pac p { margin: 2px 0; }
table { width: 100%; border-collapse: collapse; }
th, td { border-bottom: 1px solid #bbb; padding: 6px 4px; text-align: left; vertical-align: top; }
th { font-size: 11px; text-transform: uppercase; } .v { text-align: right; white-space: nowrap; }
.tot { margin: 12px 0 0 auto; width: 55%; } .tot p { display: flex; justify-content: space-between; margin: 3px 0; }
.tot .t { font-size: 15px; font-weight: bold; border-top: 1px solid #000; padding-top: 4px; }
.tot .ext { display: block; font-style: italic; color: #333; text-align: right; }
.obs { margin-top: 12px; }
.face { margin-top: 16px; break-inside: avoid; } .face h2 { font-size: 13px; margin: 0 0 8px; }
.vistas { display: flex; gap: 10px; } .vistas figure { flex: 1; margin: 0; max-width: 33%; text-align: center; }
.vistas svg { width: 100%; height: auto; border: 1px solid #ddd; border-radius: 6px; } .vistas figcaption { font-size: 10px; color: #444; }
.leg { margin-top: 8px; } .leg td, .leg th { padding: 4px; } .leg i { display: inline-block; width: 8px; height: 8px; border-radius: 4px; margin-right: 6px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.ass { display: flex; gap: 40px; margin-top: 70px; } .ass div { flex: 1; border-top: 1px solid #000; padding-top: 4px; text-align: center; }
`;
